import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking
 * Only initializes if REACT_APP_SENTRY_DSN is set in environment variables
 */
export const initSentry = () => {
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN;

  if (!sentryDsn) {
    console.log('Sentry DSN not configured, skipping Sentry initialization');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Session Replay
    // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    replaysOnErrorSampleRate: 1.0,

    // Environment
    environment: process.env.NODE_ENV || 'development',

    // Release tracking
    release: process.env.REACT_APP_VERSION || 'unknown',

    // Before send hook to filter sensitive data
    beforeSend(event, hint) {
      // Don't send errors in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Sentry event (dev mode):', event);
        return null; // Don't send in development
      }

      // Filter out sensitive data
      if (event.request) {
        delete event.request.cookies;
      }

      return event;
    },
  });
};

/**
 * Set user context for Sentry
 */
export const setSentryUser = (user: { id: number; email: string; first_name?: string; last_name?: string } | null) => {
  if (user) {
    Sentry.setUser({
      id: user.id.toString(),
      email: user.email,
      username: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Capture exception manually
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  if (context) {
    Sentry.setContext('additional', context);
  }
  Sentry.captureException(error);
};

/**
 * Capture message manually
 */
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  Sentry.captureMessage(message, level);
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (message: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message,
    data,
    level: 'info',
  });
};
